import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

export class SearchOrchestrator implements Plugin {
  private readonly inputContainer: HTMLElement | null;
  private segmentedElement: HTMLElement | null = null;

  private mainAutocompleteElement: HTMLElement | null = null;
  private modalAutocompleteElement: HTMLElement | null = null;
  private mainInput: HTMLInputElement | null = null;
  private modalInput: HTMLInputElement | null = null;

  private modal: HTMLElement | null = null;

  private activeTabId: string = "1";
  private autocompleteTabIds: string[] = [];
  private resizeTimer: any;

  constructor(private readonly element: HTMLElement) {
    this.inputContainer = element.querySelector(".search-container");
  }

  init(): void {
    this.cacheElements();
    this.bindEvents();

    if (this.segmentedElement) {
      const selectedTab = this.segmentedElement.querySelector(
        ".core-tab--selected",
      );
      this.activeTabId = selectedTab?.getAttribute("data-tab-id") || "1";

      setTimeout(() => {
        this.handleTabChange(this.activeTabId);
      }, 0);
    }
  }

  private cacheElements(): void {
    this.segmentedElement = this.element.querySelector(
      '[data-plugin="segmented-control"]',
    );

    if (this.segmentedElement) {
      const idsStr = this.segmentedElement.dataset.autocompleteTabId;
      if (idsStr) {
        try {
          const parsed = JSON.parse(idsStr);
          this.autocompleteTabIds = Array.isArray(parsed)
            ? parsed.map(String)
            : [String(parsed)];
        } catch (e) {
          this.autocompleteTabIds = idsStr.split(",").map((id) => id.trim());
        }
      } else {
        this.autocompleteTabIds = [];
      }
    }

    this.mainAutocompleteElement = this.element.querySelector(
      ".search-container[data-plugin='autocomplete']",
    );

    if (this.mainAutocompleteElement) {
      const inputSelector =
        this.mainAutocompleteElement.dataset.autocompleteInputSelector ||
        "input";
      this.mainInput = this.element.querySelector(inputSelector);
    }

    const modalId = this.element.dataset.searchModalId;
    if (modalId) {
      this.modal = document.getElementById(modalId);
    }

    if (this.modal) {
      this.modalAutocompleteElement = this.modal.querySelector(
        '[data-plugin="autocomplete"]',
      );
      this.modalInput = this.modal.querySelector("input") as HTMLInputElement;
    }
  }

  private bindEvents(): void {
    if (!this.segmentedElement || !this.mainAutocompleteElement) return;

    this.segmentedElement.addEventListener("segmentChanged", (e: Event) => {
      const detail = (e as CustomEvent).detail;
      this.activeTabId = detail.tabId;
      this.handleTabChange(this.activeTabId);
    });

    if (this.mainInput) {
      this.mainInput.addEventListener("input", (e) => this.handleMainInput(e));
      this.mainInput.addEventListener("focus", () => {
        if (
          this.isMobile() &&
          this.autocompleteTabIds.includes(this.activeTabId)
        ) {
          this.requestOpenModal();
        }
      });
    }

    if (this.modalInput) {
      this.modalInput.addEventListener("input", (e) =>
        this.handleModalInput(e),
      );
    }

    this.mainAutocompleteElement.addEventListener(
      "suggestionSelected",
      (e: Event) => {
        const item = (e as CustomEvent).detail;
        this.syncSelectionToModal(item);
      },
    );

    if (this.modalAutocompleteElement) {
      this.modalAutocompleteElement.addEventListener(
        "suggestionSelected",
        (e: Event) => {
          const item = (e as CustomEvent).detail;
          this.syncSelectionToMain(item);
          this.requestCloseModal();
        },
      );
    }

    if (this.modal) {
      this.modal.addEventListener("modal:closed", () => {
        this.handleModalClosed();
      });
    }

    document.addEventListener("click", (e) => this.handleClearClicks(e));
    window.addEventListener("resize", () => this.handleResize());
  }

  private isMobile(): boolean {
    return window.innerWidth <= 767;
  }

  private handleTabChange(tabId: string): void {
    if (this.autocompleteTabIds.includes(tabId)) {
      this.triggerAutocompleteEvent(
        this.mainAutocompleteElement,
        "autocomplete:clear",
      );
      this.triggerAutocompleteEvent(
        this.mainAutocompleteElement,
        "autocomplete:enable",
      );
      this.inputContainer?.classList.add("core-input-text--has-trailing-icon");
    } else {
      this.triggerAutocompleteEvent(
        this.mainAutocompleteElement,
        "autocomplete:clear",
      );
      this.triggerAutocompleteEvent(
        this.mainAutocompleteElement,
        "autocomplete:disable",
      );
      this.inputContainer?.classList.remove(
        "core-input-text--has-trailing-icon",
      );

      this.requestCloseModal();
    }
  }

  private handleMainInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    const value = target.value;

    if (
      this.isMobile() &&
      this.autocompleteTabIds.includes(this.activeTabId) &&
      value.length > 0
    ) {
      this.requestOpenModal();
      if (this.modalInput) {
        this.modalInput.value = value;
        this.modalInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    }
  }

  private handleModalInput(e: Event): void {
    const target = e.target as HTMLInputElement;
    if (this.mainInput) {
      this.mainInput.value = target.value;
    }
  }

  private syncSelectionToModal(item: any): void {
    if (this.modalAutocompleteElement) {
      this.modalAutocompleteElement.dispatchEvent(
        new CustomEvent("autocomplete:set-item", { detail: item }),
      );
    }
  }

  private syncSelectionToMain(item: any): void {
    if (this.mainAutocompleteElement) {
      this.mainAutocompleteElement.dispatchEvent(
        new CustomEvent("autocomplete:set-item", { detail: item }),
      );
    }
  }

  private requestOpenModal(): void {
    if (!this.modal) return;
    this.modal.dispatchEvent(new CustomEvent("modal:open"));
    setTimeout(() => {
      if (this.modalInput) this.modalInput.focus();
    }, 50);
  }

  private requestCloseModal(): void {
    if (this.modal) {
      this.modal.dispatchEvent(new CustomEvent("modal:close"));
    }
  }

  private handleModalClosed(): void {
    if (this.modalInput && this.mainInput) {
      if (this.mainInput.value !== this.modalInput.value) {
        this.mainInput.value = this.modalInput.value;
      }
    }
  }

  private handleClearClicks(e: Event): void {
    const target = e.target as HTMLElement;

    if (
      target.closest(".dsc-co-icon-close") &&
      target.closest(".core-inputText__trailing-icons")
    ) {
      e.preventDefault();
      e.stopPropagation();

      const isModal = this.modal?.contains(target);

      if (isModal) {
        this.clearInputAndSuggestions(
          this.modalInput,
          this.modalAutocompleteElement,
        );
        this.clearInputAndSuggestions(
          this.mainInput,
          this.mainAutocompleteElement,
        );
      } else if (this.element.contains(target)) {
        this.clearInputAndSuggestions(
          this.mainInput,
          this.mainAutocompleteElement,
        );
        this.clearInputAndSuggestions(
          this.modalInput,
          this.modalAutocompleteElement,
        );
      }
    }
  }

  private clearInputAndSuggestions(
    input: HTMLInputElement | null,
    autocompleteContainer: HTMLElement | null,
  ): void {
    if (input) {
      input.value = "";
      input.dispatchEvent(new Event("input", { bubbles: true }));
    }

    if (autocompleteContainer) {
      this.triggerAutocompleteEvent(
        autocompleteContainer,
        "autocomplete:clearSelection",
      );
      const suggestionsList = autocompleteContainer.querySelector(
        "#suggestions-container",
      );
      if (suggestionsList) {
        suggestionsList.classList.add("search-container__hidden");
        suggestionsList.classList.remove("search-container__visible");
      }
    }
  }

  private triggerAutocompleteEvent(
    element: HTMLElement | null,
    eventName: string,
  ): void {
    if (element) {
      element.dispatchEvent(new CustomEvent(eventName));
    }
  }

  private handleResize(): void {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      if (!this.isMobile()) {
        this.requestCloseModal();
      }
    }, 250);
  }
}

PluginRegistry.register("search-orchestrator", SearchOrchestrator);
